// Server-backed SessionStore for the deployed build.
//
// Implements the same `SessionStore` interface as `localSessionStore`, talking
// to the sessions API (on the same API Gateway as the feedback endpoint, behind
// the Cognito authorizer). This is the swap that makes conversation history
// per-user and durable in the cloud; the sidebar, hook, and `ChatInterface` do
// not change.
//
// Auth uses the Cognito **ID token** (the API Gateway Cognito authorizer
// validates ID tokens, same as `feedbackService`). The user is identified
// server-side from that token, so no owner id is sent from the client.

import type { ChatSession, Message, SessionSummary } from "@/components/chat/types"
import type { SessionStore } from "./sessionStore"

/** Dependencies injected into the remote store (kept explicit for testability). */
export interface RemoteSessionStoreDeps {
  /**
   * Resolve the API base URL (the REST API root, ending in "/"). Defaults to
   * reading `feedbackApiUrl` from `aws-exports.json`.
   */
  loadBaseUrl?: () => Promise<string>
  /** Return the current Cognito ID token, or undefined when not signed in. */
  getIdToken: () => string | undefined
}

// Cache the API base URL across calls (it never changes within a session).
let cachedBaseUrl = ""

/**
 * Default base-URL loader: read the REST API root from `aws-exports.json`.
 *
 * The sessions routes live on the same REST API as the feedback endpoint, so we
 * reuse its published root (`feedbackApiUrl`, which ends in "/").
 *
 * @returns the API base URL.
 * @throws if the config cannot be loaded or the URL is missing.
 */
async function defaultLoadBaseUrl(): Promise<string> {
  if (cachedBaseUrl) return cachedBaseUrl
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  if (!config.feedbackApiUrl) {
    throw new Error("Sessions API URL not configured (feedbackApiUrl missing)")
  }
  cachedBaseUrl = config.feedbackApiUrl as string
  return cachedBaseUrl
}

/**
 * Create a server-backed `SessionStore`.
 *
 * @param deps - the ID-token getter and (optionally) a base-URL loader.
 * @returns a `SessionStore` bound to the deployed sessions API.
 */
export function createRemoteSessionStore(deps: RemoteSessionStoreDeps): SessionStore {
  const loadBaseUrl = deps.loadBaseUrl ?? defaultLoadBaseUrl

  /**
   * Issue an authenticated request to a sessions endpoint and parse JSON.
   *
   * @param path - path appended to the API base (e.g. "sessions" or "sessions/<id>").
   * @param init - optional fetch init (method, body).
   * @returns the parsed JSON response.
   * @throws if unauthenticated or the response is not ok.
   */
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const idToken = deps.getIdToken()
    if (!idToken) {
      throw new Error("Se requiere autenticación para el historial de conversaciones.")
    }
    const base = await loadBaseUrl()
    const response = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        ...(init?.headers ?? {}),
      },
    })
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}))
      throw new Error(detail.error || `HTTP ${response.status}`)
    }
    return (await response.json()) as T
  }

  return {
    async list(): Promise<SessionSummary[]> {
      const data = await request<{ sessions: SessionSummary[] }>("sessions")
      return data.sessions ?? []
    },

    async get(id: string): Promise<ChatSession | null> {
      try {
        const data = await request<{ id: string; title?: string; messages: Message[] }>(
          `sessions/${encodeURIComponent(id)}`
        )
        // The server already maps Memory events to the frontend message shape.
        // createdAt/updatedAt are not needed to render a reopened transcript.
        return {
          id: data.id,
          title: data.title ?? "",
          messages: data.messages ?? [],
          createdAt: "",
          updatedAt: "",
        }
      } catch {
        // A missing/expired session reads as "not found" rather than an error
        // banner — the sidebar simply opens an empty thread.
        return null
      }
    },

    async save(): Promise<void> {
      // No-op in the cloud: the runtime persists every turn to AgentCore Memory
      // as it happens, so there is nothing for the client to push. The sidebar
      // refreshes from the server after a turn (see useSessions).
    },

    async remove(id: string): Promise<void> {
      await request(`sessions/${encodeURIComponent(id)}`, { method: "DELETE" })
    },

    async rename(id: string, title: string): Promise<void> {
      await request(`sessions/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      })
    },

    async touch(id: string): Promise<void> {
      // Bump the index row's recency (updatedAt + ttl) without touching the
      // title. The same PATCH route as rename: a title-less body signals a
      // touch server-side (see the sessions Lambda). Needed because `save` is a
      // no-op in the cloud, so a normal turn would otherwise never advance
      // recency and the sidebar order would look stale.
      await request(`sessions/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ touch: true }),
      })
    },
  }
}
